import { useHandleServiceSubjectPress } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/functions/useHandleServiceSubjectPress';
import { useServiceSubjectsHost } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/host';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ServiceSubjectType } from '@euler/model/enum';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { useCallback, useMemo } from 'react';
import { SectionList, SectionListRenderItem } from 'react-native';
import { findServiceSubjectIndex } from '../functions/findServiceSubjectIndex';
import { serviceSubjectItemKeyExtractor } from '../functions/serviceSubjectItemKeyExtractor';
import { useSelectedServiceSubjectsBottomSheetFooterHeight } from '../functions/useSelectedServiceSubjectsBottomSheetFooterHeight';
import { ServiceSubjectCategory, ServiceSubjectItem } from '../types';
import { ServiceSubjectCell } from './ServiceSubjectCell';
import { ServiceSubjectSectionHeader } from './ServiceSubjectSectionHeader';

type Section = {
  title: string;
  showItemNumber?: boolean;
  data: ServiceSubjectItem[];
};

const kStandaloneSearch = true;

export const ServiceSubjectTabContent = wrapNavigatorScreen(
  ({ category }: { category: ServiceSubjectCategory }) => {
    const { rpx } = useResponsiveContext();
    const { orderDetail$, serviceSubjectQuery$, capabilities } =
      useServiceSubjectsHost();
    const [orderDetail] = useBehaviorSubject(orderDetail$);
    const [keyword] = useBehaviorSubject(serviceSubjectQuery$);

    const isSubjectReadonly = useCallback(
      (item: ServiceSubjectItem) => {
        return Boolean(
          (item.type === ServiceSubjectType.Inspection &&
            capabilities?.readonlyInspectionSubjects.has(item.title)) ||
            (item.type === ServiceSubjectType.Construction &&
              capabilities?.readonlyConstructionSubjects.has(item.title)),
        );
      },
      [
        capabilities?.readonlyConstructionSubjects,
        capabilities?.readonlyInspectionSubjects,
      ],
    );

    const sections = useMemo(
      () =>
        categoryToSections(category, kStandaloneSearch ? undefined : keyword),
      [category, keyword],
    );

    const onItemPress = useHandleServiceSubjectPress({ isSubjectReadonly });
    const bottomSheetFooterHeight =
      useSelectedServiceSubjectsBottomSheetFooterHeight();

    const renderItem: SectionListRenderItem<ServiceSubjectItem, Section> =
      useCallback(
        ({ item, section, index }) => (
          <ServiceSubjectCell
            item={item}
            selected={findServiceSubjectIndex(orderDetail, item) >= 0}
            onPress={onItemPress}
            first={index === 0}
            last={index === section.data.length - 1}
            readonly={isSubjectReadonly(item)}
          />
        ),
        [isSubjectReadonly, onItemPress, orderDetail],
      );

    return (
      <>
        <SectionList
          sections={sections}
          keyExtractor={serviceSubjectItemKeyExtractor}
          stickySectionHeadersEnabled={true}
          initialNumToRender={18}
          contentContainerStyle={{
            paddingBottom: bottomSheetFooterHeight + rpx(15),
          }}
          showsVerticalScrollIndicator={false}
          renderSectionHeader={({ section }) => (
            <ServiceSubjectSectionHeader
              title={section.title}
              count={section.showItemNumber ? section.data.length : undefined}
            />
          )}
          renderItem={renderItem}
          css={`
            flex: 1;
            background-color: #f3f3f3;
          `}
        />
      </>
    );
  },
);

function categoryToSections(
  category: ServiceSubjectCategory,
  keyword?: string,
) {
  const sections: Section[] = [];
  for (const group of category.groups) {
    if (!group.items.length) continue;
    sections.push({
      title: group.title,
      data: keyword
        ? group.items.filter(
            x => x.title.includes(keyword) || x.pyInitials?.includes(keyword),
          )
        : group.items,
      showItemNumber: group.showItemNumber,
    });
  }
  return sections;
}
